import React from 'react';
import { tokens } from '../tokens';

// Professional Badge component replacing scattered badge styles
const Badge = ({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  className = '',
  ...props
}) => {
  // Base badge styles
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: tokens.typography.fontFamily.sans.join(', '),
    fontWeight: tokens.typography.fontWeight.medium,
    borderRadius: tokens.borderRadius.full,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
  };

  // Size variants
  const sizes = {
    sm: {
      padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
      fontSize: tokens.typography.fontSize.xs[0],
      lineHeight: tokens.typography.fontSize.xs[1].lineHeight,
    },
    md: {
      padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
      fontSize: tokens.typography.fontSize.sm[0],
      lineHeight: tokens.typography.fontSize.sm[1].lineHeight,
    },
    lg: {
      padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
      fontSize: tokens.typography.fontSize.base[0],
      lineHeight: tokens.typography.fontSize.base[1].lineHeight,
    },
  };

  // Cottagecore badge colors
  const variants = {
    primary: {
      backgroundColor: 'rgba(234,124,31,0.12)',
      color: '#C25F0E',
      borderColor: 'rgba(234,124,31,0.3)',
    },
    success: {
      backgroundColor: 'rgba(107,122,62,0.12)',
      color: '#6B7A3E',
      borderColor: 'rgba(107,122,62,0.3)',
    },
    warning: {
      backgroundColor: 'rgba(234,124,31,0.12)',
      color: '#C25F0E',
      borderColor: 'rgba(234,124,31,0.3)',
    },
    error: {
      backgroundColor: 'rgba(193,74,51,0.1)',
      color: '#C14A33',
      borderColor: 'rgba(193,74,51,0.25)',
    },
    info: {
      backgroundColor: 'rgba(163,144,104,0.12)',
      color: '#6E5A38',
      borderColor: 'rgba(163,144,104,0.3)',
    },
    neutral: {
      backgroundColor: 'var(--color-surface, #EEE2BF)',
      color: 'var(--color-text-muted, #6E5A38)',
      borderColor: 'var(--color-border, #D7C9A1)',
    },
    // Status-specific badges
    completed: {
      backgroundColor: 'rgba(107,122,62,0.12)',
      color: '#6B7A3E',
      borderColor: 'rgba(107,122,62,0.3)',
    },
    'in_progress': {
      backgroundColor: 'rgba(234,124,31,0.12)',
      color: '#C25F0E',
      borderColor: 'rgba(234,124,31,0.3)',
    },
    pending: {
      backgroundColor: 'var(--color-surface, #EEE2BF)',
      color: 'var(--color-text-muted, #6E5A38)',
      borderColor: 'var(--color-border, #D7C9A1)',
    },
    failed: {
      backgroundColor: 'rgba(193,74,51,0.1)',
      color: '#C14A33',
      borderColor: 'rgba(193,74,51,0.25)',
    },
  };

  // Combine all styles
  const badgeStyles = {
    ...baseStyles,
    ...sizes[size],
    ...variants[variant],
  };

  return (
    <span
      style={badgeStyles}
      className={className}
      {...props}
    >
      {Icon && (
        <Icon 
          size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14}
          style={{ marginRight: tokens.spacing[1] }}
        />
      )}
      {children}
    </span>
  );
};

// Map status to badge variant (for project status display)
export const getStatusVariant = (status) => {
  const statusMap = {
    completed: 'completed',
    processing: 'in_progress',
    submitted: 'pending',
    error: 'failed',
    in_progress: 'in_progress',
    pending: 'pending',
    failed: 'failed',
    rejected: 'failed',
  };
  return statusMap[status] || 'neutral';
};

export default Badge;